import { FC, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import jsPDF from 'jspdf'
import { Panel } from '../components/ui/Panel'
import { Button } from '../components/ui/Button'
import { PageHeader } from '../components/ui/PageHeader'
import { useAppSelector } from '../app/hooks'
import { useAppDispatch } from '../app/hooks'
import { removeItem, updateQuantity, clearCart } from '../app/cartSlice'
import { merchImagesByProduct } from '../data/merch-images'
import { PayPalButtons } from '@paypal/react-paypal-js'

const FORMSPREE_ENDPOINT = 'https://formspree.io/f/xdajoadv'

const sendToFormspree = async (data: object) => {
  await fetch(FORMSPREE_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(data),
  })
}

/** Cart page that guides users through a basic checkout flow. */
const Cart: FC = () => {
  const items = useAppSelector((state) => state.cart.items)
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [step, setStep] = useState(1)
  const [confirmedOrderId, setConfirmedOrderId] = useState<string | null>(null)
  const [confirmedSnapshot, setConfirmedSnapshot] = useState<{
    items: typeof items
    subtotal: number
    shippingFee: number
    total: number
    date: string
  } | null>(null)
  const [form, setForm] = useState({
    email: '',
    fullName: '',
    address: '',
    city: '',
    country: '',
    postalCode: '',
    delivery: 'standard',
    region: 'uk' as 'uk' | 'international',
  })
  const [errors, setErrors] = useState<Partial<typeof form>>({})

  const validateDetails = () => {
    const next: Partial<typeof form> = {}
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      next.email = 'We need a valid email address to send your order confirmation.'
    if (form.fullName.trim().split(/\s+/).filter(Boolean).length < 2)
      next.fullName = 'We need your full name (first and last) to ship your order.'
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const validateShipping = () => {
    const next: Partial<typeof form> = {}
    if (!form.address.trim()) next.address = 'We need your street address to ship your order.'
    if (!form.city.trim()) next.city = 'We need your city to ship your order.'
    if (!form.country.trim()) next.country = 'We need your country to ship your order.'
    if (!form.postalCode.trim()) next.postalCode = 'We need your postal code to ship your order.'
    setErrors(next)
    return Object.keys(next).length === 0
  }

  // TODO: Confirm shipping fees and replace placeholders.
  const shippingFees = {
    uk: { standard: 3.95, express: 7.95 },
    international: { standard: 9.95, express: 14.95 },
  }

  const subtotal = useMemo(() => {
    return items.reduce((sum, item) => {
      const numeric = Number(item.price.replace(/[^0-9.]/g, '')) || 0
      return sum + numeric * item.quantity
    }, 0)
  }, [items])

  // Items flagged with freeShipping carry no postage. When every item in the
  // cart is free-shipping, the order ships free regardless of region/speed.
  const isFreeShipping = items.length > 0 && items.every((item) => item.freeShipping)
  const shippingFee = isFreeShipping
    ? 0
    : shippingFees[form.region][form.delivery as 'standard' | 'express']
  const total = subtotal + shippingFee

  return (
    <div className='space-y-6'>
      <PageHeader
        title={step === 5 ? 'Order Confirmed' : 'Cart'}
        subtitle={
          step === 5
            ? 'Your order has been received.'
            : 'Review selected items and complete checkout.'
        }
      />

      <Panel>
        <div className='flex flex-wrap gap-2 text-sm uppercase tracking-[0.3em] text-brand-blue/70'>
          {step < 5 ? (
            <>
              <span className={step === 1 ? 'text-brand-blue' : ''}>Cart</span>
              <span>•</span>
              <span className={step === 2 ? 'text-brand-blue' : ''}>Details</span>
              <span>•</span>
              <span className={step === 3 ? 'text-brand-blue' : ''}>Shipping</span>
              <span>•</span>
              <span className={step === 4 ? 'text-brand-blue' : ''}>Payment</span>
            </>
          ) : (
            <span className='text-brand-blue'>Confirmed</span>
          )}
        </div>

        {step === 1 && (
          <>
            <div className='mt-5 space-y-3 text-sm text-brand-azure/80'>
              {items.length === 0 && (
                <div className='rounded-xl bg-brand-smoky/80 p-4 shadow-[0_12px_30px_rgba(0,0,0,0.35)] ring-1 ring-black/20'>
                  Your cart is empty.
                </div>
              )}
              {items.map((item) => (
                <div
                  key={`${item.id}-${item.size ?? 'na'}`}
                  className='flex flex-wrap items-center justify-between gap-4 rounded-xl bg-brand-smoky/80 p-4 shadow-[0_12px_30px_rgba(0,0,0,0.35)] ring-1 ring-black/20'
                >
                  <div className='flex items-center gap-4'>
                    <div className='h-14 w-14 overflow-hidden rounded-lg bg-brand-smoky/80 ring-1 ring-black/20'>
                      {merchImagesByProduct[item.id]?.[0] ? (
                        <img
                          src={merchImagesByProduct[item.id][0]}
                          alt={item.name}
                          className='h-full w-full object-cover'
                        />
                      ) : (
                        <div className='flex h-full w-full items-center justify-center text-sm uppercase tracking-[0.3em] text-brand-azure/60'>
                          Image
                        </div>
                      )}
                    </div>
                    <div>
                      <p className='text-brand-blue'>{item.name}</p>
                      {item.size && (
                        <p className='text-sm uppercase tracking-[0.3em] text-brand-blue/70'>
                          Size {item.size}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className='flex items-center gap-4'>
                    <div className='text-right'>
                      <p className='text-brand-blue'>{item.price}</p>
                      <p className='text-sm text-brand-blue/70'>Qty {item.quantity}</p>
                    </div>
                    <div className='flex items-center gap-2'>
                      <Button
                        variant='secondary'
                        size='sm'
                        onClick={() =>
                          dispatch(
                            updateQuantity({
                              id: item.id,
                              size: item.size,
                              quantity: item.quantity - 1,
                            }),
                          )
                        }
                      >
                        -
                      </Button>
                      <Button
                        variant='secondary'
                        size='sm'
                        onClick={() =>
                          dispatch(
                            updateQuantity({
                              id: item.id,
                              size: item.size,
                              quantity: item.quantity + 1,
                            }),
                          )
                        }
                      >
                        +
                      </Button>
                      <Button
                        variant='ghost'
                        size='sm'
                        onClick={() => dispatch(removeItem({ id: item.id, size: item.size }))}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {items.length > 0 && (
              <>
                <div className='mt-6 grid gap-2 border-t border-black/30 pt-4 text-sm text-brand-azure/80'>
                  <div className='flex items-center justify-between'>
                    <span className='uppercase tracking-[0.3em] text-brand-blue/70'>Subtotal</span>
                    <span className='text-brand-blue'>£{subtotal.toFixed(2)}</span>
                  </div>
                  <div className='flex items-center justify-between'>
                    <span className='uppercase tracking-[0.3em] text-brand-blue/70'>Delivery</span>
                    <span className='text-brand-blue'>£{shippingFee.toFixed(2)}</span>
                  </div>
                  <div className='flex items-center justify-between pt-2'>
                    <span className='uppercase tracking-[0.3em] text-brand-blue/70'>Total</span>
                    <span className='text-lg font-semibold text-brand-blue'>
                      £{total.toFixed(2)}
                    </span>
                  </div>
                </div>

                <div className='mt-5'>
                  <Button variant='primary' size='md' onClick={() => setStep(2)}>
                    Continue to Details
                  </Button>
                </div>
              </>
            )}
          </>
        )}

        {step === 2 && (
          <>
            <div className='mt-5 grid gap-4 text-sm text-brand-azure/80'>
              <label className='grid gap-2'>
                Email
                <input
                  className={`rounded-xl bg-brand-smoky px-4 py-2 text-brand-azure ring-1 ${errors.email ? 'ring-red-500' : 'ring-black/30'}`}
                  value={form.email}
                  onChange={(event) => {
                    setForm((data) => ({ ...data, email: event.target.value }))
                    setErrors((e) => ({ ...e, email: undefined }))
                  }}
                />
                {errors.email && <p className='text-sm text-red-400'>{errors.email}</p>}
              </label>
              <label className='grid gap-2'>
                Full Name
                <input
                  className={`rounded-xl bg-brand-smoky px-4 py-2 text-brand-azure ring-1 ${errors.fullName ? 'ring-red-500' : 'ring-black/30'}`}
                  value={form.fullName}
                  onChange={(event) => {
                    setForm((data) => ({ ...data, fullName: event.target.value }))
                    setErrors((e) => ({ ...e, fullName: undefined }))
                  }}
                />
                {errors.fullName && <p className='text-sm text-red-400'>{errors.fullName}</p>}
              </label>
            </div>
            <div className='mt-5 flex items-center gap-3 cta-stack'>
              <Button variant='secondary' size='sm' onClick={() => setStep(1)}>
                Back
              </Button>
              <Button
                variant='primary'
                size='md'
                onClick={() => {
                  if (validateDetails()) setStep(3)
                }}
              >
                Continue to Shipping
              </Button>
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <div className='mt-5 grid gap-4 text-sm text-brand-azure/80'>
              <div className='grid gap-2'>
                Delivery Region
                <div className='flex rounded-xl overflow-hidden ring-1 ring-black/30'>
                  {(['uk', 'international'] as const).map((r) => (
                    <button
                      key={r}
                      type='button'
                      className='flex-1 px-4 py-2 text-sm font-semibold uppercase tracking-[0.3em] transition-colors duration-150'
                      style={
                        form.region === r
                          ? { backgroundColor: '#F8D309', color: '#191102' }
                          : { backgroundColor: '#2a2015', color: '#DEF1F8', opacity: 0.5 }
                      }
                      onClick={() => setForm((d) => ({ ...d, region: r }))}
                    >
                      {r === 'uk' ? 'United Kingdom' : 'Outside UK'}
                    </button>
                  ))}
                </div>
              </div>
              <label className='grid gap-2'>
                Address
                <input
                  className={`rounded-xl bg-brand-smoky px-4 py-2 text-brand-azure ring-1 ${errors.address ? 'ring-red-500' : 'ring-black/30'}`}
                  value={form.address}
                  onChange={(event) => {
                    setForm((data) => ({ ...data, address: event.target.value }))
                    setErrors((e) => ({ ...e, address: undefined }))
                  }}
                />
                {errors.address && <p className='text-sm text-red-400'>{errors.address}</p>}
              </label>
              <div className='grid gap-4 sm:grid-cols-3'>
                <label className='grid gap-2'>
                  City
                  <input
                    className={`rounded-xl bg-brand-smoky px-4 py-2 text-brand-azure ring-1 ${errors.city ? 'ring-red-500' : 'ring-black/30'}`}
                    value={form.city}
                    onChange={(event) => {
                      setForm((data) => ({ ...data, city: event.target.value }))
                      setErrors((e) => ({ ...e, city: undefined }))
                    }}
                  />
                  {errors.city && <p className='text-sm text-red-400'>{errors.city}</p>}
                </label>
                <label className='grid gap-2'>
                  Country
                  <input
                    className={`rounded-xl bg-brand-smoky px-4 py-2 text-brand-azure ring-1 ${errors.country ? 'ring-red-500' : 'ring-black/30'}`}
                    value={form.country}
                    onChange={(event) => {
                      setForm((data) => ({ ...data, country: event.target.value }))
                      setErrors((e) => ({ ...e, country: undefined }))
                    }}
                  />
                  {errors.country && <p className='text-sm text-red-400'>{errors.country}</p>}
                </label>
                <label className='grid gap-2'>
                  Postal Code
                  <input
                    className={`rounded-xl bg-brand-smoky px-4 py-2 text-brand-azure ring-1 ${errors.postalCode ? 'ring-red-500' : 'ring-black/30'}`}
                    value={form.postalCode}
                    onChange={(event) => {
                      setForm((data) => ({ ...data, postalCode: event.target.value }))
                      setErrors((e) => ({ ...e, postalCode: undefined }))
                    }}
                  />
                  {errors.postalCode && <p className='text-sm text-red-400'>{errors.postalCode}</p>}
                </label>
              </div>
              <label className='grid gap-2'>
                Delivery Speed
                <select
                  className='rounded-xl bg-brand-smoky px-4 py-2 text-brand-azure ring-1 ring-black/30'
                  value={form.delivery}
                  onChange={(event) =>
                    setForm((data) => ({ ...data, delivery: event.target.value }))
                  }
                >
                  <option value='standard'>
                    Standard (3-5 days) —{' '}
                    {isFreeShipping ? 'Free' : `£${shippingFees[form.region].standard.toFixed(2)}`}
                  </option>
                  <option value='express'>
                    Express (1-2 days) —{' '}
                    {isFreeShipping ? 'Free' : `£${shippingFees[form.region].express.toFixed(2)}`}
                  </option>
                </select>
              </label>
            </div>
            <div className='mt-5 flex items-center gap-3 cta-stack'>
              <Button variant='secondary' size='sm' onClick={() => setStep(2)}>
                Back
              </Button>
              <Button
                variant='primary'
                size='md'
                onClick={() => {
                  if (validateShipping()) setStep(4)
                }}
              >
                Continue to Payment
              </Button>
            </div>
          </>
        )}

        {step === 4 && (
          <>
            <div className='mt-6 grid gap-2 border-t border-black/30 pt-4 text-sm text-brand-azure/80'>
              <div className='flex items-center justify-between'>
                <span className='uppercase tracking-[0.3em] text-brand-blue/70'>Subtotal</span>
                <span className='text-brand-blue'>£{subtotal.toFixed(2)}</span>
              </div>
              <div className='flex items-center justify-between'>
                <span className='uppercase tracking-[0.3em] text-brand-blue/70'>Delivery</span>
                <span className='text-brand-blue'>£{shippingFee.toFixed(2)}</span>
              </div>
              <div className='flex items-center justify-between pt-2'>
                <span className='uppercase tracking-[0.3em] text-brand-blue/70'>Total</span>
                <span className='text-lg font-semibold text-brand-blue'>£{total.toFixed(2)}</span>
              </div>
            </div>
            <div className='mt-5 grid gap-4 text-sm text-brand-azure/80'>
              <div className='rounded-xl bg-brand-smoky/80 p-4 shadow-[0_12px_30px_rgba(0,0,0,0.35)] ring-1 ring-black/20'>
                <p className='text-sm uppercase tracking-[0.3em] text-brand-blue/70'>Payment</p>
                <p className='mt-2 mb-4 text-brand-azure/80'>
                  Pay securely with PayPal. Clicking the button below will record your order and
                  open PayPal checkout.
                </p>
                <div className='max-w-xs'>
                  <PayPalButtons
                    style={{ color: 'gold', shape: 'rect', label: 'pay', height: 40 }}
                    createOrder={async (_data, actions) => {
                      await sendToFormspree({
                        status: 'pending',
                        email: form.email,
                        fullName: form.fullName,
                        address: form.address,
                        city: form.city,
                        country: form.country,
                        postalCode: form.postalCode,
                        delivery: form.delivery,
                        items: items.map((i) => ({
                          name: i.name,
                          size: i.size,
                          price: i.price,
                          quantity: i.quantity,
                        })),
                        subtotal: `£${subtotal.toFixed(2)}`,
                        shippingFee: `£${shippingFee.toFixed(2)}`,
                        total: `£${total.toFixed(2)}`,
                      })
                      return actions.order.create({
                        intent: 'CAPTURE',
                        purchase_units: [
                          { amount: { value: total.toFixed(2), currency_code: 'GBP' } },
                        ],
                      })
                    }}
                    onApprove={async (data, actions) => {
                      const capture = await actions.order!.capture()
                      const transactionId =
                        capture.purchase_units?.[0]?.payments?.captures?.[0]?.id ?? data.orderID
                      await sendToFormspree({
                        status: 'paid',
                        paypalOrderId: data.orderID,
                        paypalTransactionId: transactionId,
                        email: form.email,
                        fullName: form.fullName,
                        address: form.address,
                        city: form.city,
                        country: form.country,
                        postalCode: form.postalCode,
                        delivery: form.delivery,
                        items: items.map((i) => ({
                          name: i.name,
                          size: i.size,
                          price: i.price,
                          quantity: i.quantity,
                        })),
                        subtotal: `£${subtotal.toFixed(2)}`,
                        shippingFee: `£${shippingFee.toFixed(2)}`,
                        total: `£${total.toFixed(2)}`,
                      })
                      setConfirmedOrderId(transactionId)
                      setConfirmedSnapshot({
                        items: [...items],
                        subtotal,
                        shippingFee,
                        total,
                        date: new Date().toLocaleDateString('en-GB', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                        }),
                      })
                      dispatch(clearCart())
                      setStep(5)
                    }}
                  />
                </div>
              </div>
            </div>
            <div className='mt-5 flex items-center gap-3 cta-stack'>
              <Button variant='secondary' size='sm' onClick={() => setStep(3)}>
                Back
              </Button>
            </div>
          </>
        )}

        {step === 5 && (
          <>
            <div className='mt-5 rounded-xl bg-brand-smoky/80 p-6 shadow-[0_12px_30px_rgba(0,0,0,0.35)] ring-1 ring-black/20 text-sm text-brand-azure/80'>
              <p className='mt-2 text-brand-blue font-semibold'>Thank you, {form.fullName}!</p>
              <p className='mt-1'>Your payment was successful and your order has been received.</p>
            </div>
            <div className='mt-5 rounded-xl bg-brand-smoky/80 p-4 shadow-[0_12px_30px_rgba(0,0,0,0.35)] ring-1 ring-black/20 text-sm text-brand-azure/80'>
              <p className='text-brand-azure/80'>
                Download your order confirmation and keep it for your records. If you have any
                issues with your order, use this document to reach out via our{' '}
                <a href='/contact' className='underline hover:text-brand-gold'>
                  contact form
                </a>
                .
              </p>
              <div className='mt-3'>
                <Button
                  variant='secondary'
                  size='sm'
                  onClick={() => {
                    if (!confirmedSnapshot || !confirmedOrderId) return
                    const doc = new jsPDF()
                    const lh = 7
                    let y = 20

                    const line = (text: string, indent = 14, bold = false) => {
                      doc.setFont('helvetica', bold ? 'bold' : 'normal')
                      doc.text(text, indent, y)
                      y += lh
                    }
                    const gap = (n = 1) => {
                      y += lh * n
                    }
                    const rule = () => {
                      doc.setDrawColor(180)
                      doc.line(14, y, 196, y)
                      y += 4
                    }

                    doc.setFontSize(18)
                    doc.setFont('helvetica', 'bold')
                    doc.text('Ukraine Metal Festival', 14, y)
                    y += 9
                    doc.setFontSize(11)
                    doc.setFont('helvetica', 'normal')
                    doc.setTextColor(100)
                    doc.text('Order Confirmation', 14, y)
                    doc.setTextColor(0)
                    y += 5
                    rule()

                    doc.setFontSize(10)
                    line(`Transaction ID: ${confirmedOrderId}`, 14, true)
                    line(`Date: ${confirmedSnapshot.date}`)
                    gap(0.5)
                    line(`Name: ${form.fullName}`)
                    line(`Email: ${form.email}`)
                    line(
                      `Address: ${form.address}, ${form.city}, ${form.postalCode}, ${form.country}`,
                    )
                    line(
                      `Delivery: ${form.delivery === 'express' ? 'Express (1-2 days)' : 'Standard (3-5 days)'} — ${form.region === 'uk' ? 'United Kingdom' : 'Outside UK'}`,
                    )
                    gap()
                    rule()

                    doc.setFont('helvetica', 'bold')
                    doc.text('Item', 14, y)
                    doc.text('Qty', 130, y)
                    doc.text('Price', 160, y)
                    y += lh
                    doc.setFont('helvetica', 'normal')
                    confirmedSnapshot.items.forEach((item) => {
                      const label = item.size ? `${item.name} (${item.size})` : item.name
                      doc.text(doc.splitTextToSize(label, 110)[0], 14, y)
                      doc.text(String(item.quantity), 130, y)
                      doc.text(item.price, 160, y)
                      y += lh
                    })
                    gap(0.5)
                    rule()

                    line(`Subtotal: £${confirmedSnapshot.subtotal.toFixed(2)}`)
                    line(`Shipping: £${confirmedSnapshot.shippingFee.toFixed(2)}`)
                    gap(0.3)
                    doc.setFontSize(11)
                    line(`Total: £${confirmedSnapshot.total.toFixed(2)}`, 14, true)

                    doc.save(`UMF-order-${confirmedOrderId}.pdf`)
                  }}
                >
                  Download Order Confirmation
                </Button>
              </div>
            </div>
            <div className='mt-5'>
              <Button variant='primary' size='md' onClick={() => navigate('/merch')}>
                Back to Merch
              </Button>
            </div>
          </>
        )}
      </Panel>
    </div>
  )
}

export default Cart
